import React from "react";

export const Card = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`rounded-2xl border border-slate-200 bg-white p-5 soft-shadow ${className}`}>
    {children}
  </div>
);

export const PageHeader = ({ title, body, action, testId }) => (
  <div data-testid={testId} className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">{title}</h1>
      {body && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">{body}</p>}
    </div>
    {action}
  </div>
);

export const Stat = ({ label, value, hint, testId }) => (
  <Card testId={testId}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-2 font-display text-2xl font-bold text-slate-900">{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </Card>
);

const statusColor = (status) => {
  const map = {
    "Information Required": "bg-amber-50 text-amber-800 border-amber-200",
    "Content Being Prepared": "bg-sky-50 text-sky-800 border-sky-200",
    "Ready for Review": "bg-violet-50 text-violet-800 border-violet-200",
    "Changes Requested": "bg-orange-50 text-orange-800 border-orange-200",
    "Changes In Progress": "bg-orange-50 text-orange-800 border-orange-200",
    Approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    Completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    Draft: "bg-slate-100 text-slate-700 border-slate-200",
    "Needs Review": "bg-sky-50 text-sky-800 border-sky-200",
    "Ready for Customer": "bg-violet-50 text-violet-800 border-violet-200",
    "Customer Reviewing": "bg-violet-50 text-violet-800 border-violet-200",
    "Revision Requested": "bg-orange-50 text-orange-800 border-orange-200",
    "Publishing Failed": "bg-red-50 text-red-700 border-red-200",
    Scheduled: "bg-indigo-50 text-indigo-800 border-indigo-200",
    Published: "bg-emerald-50 text-emerald-800 border-emerald-200",
    connected: "bg-emerald-50 text-emerald-800 border-emerald-200",
    expired: "bg-amber-50 text-amber-800 border-amber-200",
    disconnected: "bg-slate-100 text-slate-600 border-slate-200",
    open: "bg-amber-50 text-amber-800 border-amber-200",
    answered: "bg-sky-50 text-sky-800 border-sky-200",
    closed: "bg-slate-100 text-slate-600 border-slate-200",
    "Waiting for us": "bg-amber-50 text-amber-800 border-amber-200",
    "We replied": "bg-sky-50 text-sky-800 border-sky-200",
    Closed: "bg-slate-100 text-slate-600 border-slate-200",
    "AI Draft": "bg-slate-100 text-slate-700 border-slate-200",
    completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
    submitted: "bg-sky-50 text-sky-800 border-sky-200",
  };
  return map[status] || "bg-slate-100 text-slate-700 border-slate-200";
};

export const StatusPill = ({ status, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusColor(status)}`}
  >
    {status}
  </span>
);

export const Field = ({ label, children, error, htmlFor, hint }) => (
  <div>
    {label && (
      <label htmlFor={htmlFor} className="text-sm font-semibold text-slate-800">
        {label}
      </label>
    )}
    <div className="mt-2">{children}</div>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
  </div>
);

export const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 outline-none transition-colors focus:border-[#F95738] focus:ring-2 focus:ring-[#F95738]/20";

export const Btn = ({ children, variant = "primary", className = "", testId, ...rest }) => {
  const styles = {
    primary: "bg-[#F95738] text-white hover:bg-[#E0482B]",
    dark: "bg-slate-900 text-white hover:bg-slate-800",
    outline: "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50",
    danger: "border border-red-200 bg-white text-red-700 hover:bg-red-50",
    ghost: "text-slate-600 hover:bg-slate-100",
  };
  return (
    <button
      type="button"
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${styles[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

export const Empty = ({ title, body, testId }) => (
  <Card testId={testId} className="text-center">
    <p className="font-display text-lg font-semibold text-slate-900">{title}</p>
    {body && <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">{body}</p>}
  </Card>
);

export const CheckboxGroup = ({ options, value = [], onChange, name, columns = 2 }) => (
  <div className={`grid gap-2 sm:grid-cols-${columns}`}>
    {options.map((opt) => {
      const checked = value.includes(opt);
      return (
        <label
          key={opt}
          htmlFor={`${name}-${opt}`}
          className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm transition-colors ${
            checked ? "border-[#F95738] bg-[#FFF7F4] text-slate-900" : "border-slate-200 bg-white text-slate-700"
          }`}
        >
          <input
            id={`${name}-${opt}`}
            type="checkbox"
            data-testid={`${name}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="h-4 w-4 accent-[#F95738]"
            checked={checked}
            onChange={() => onChange(checked ? value.filter((v) => v !== opt) : [...value, opt])}
          />
          {opt}
        </label>
      );
    })}
  </div>
);

export const RadioGroupPills = ({ options, value, onChange, name }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`${name}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onChange(opt)}
        aria-pressed={value === opt}
        className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
          value === opt
            ? "border-slate-900 bg-slate-900 text-white"
            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);
